<template>
  <div class="time-picker-container">
    <div class="time-picker">
      <label>{{ $t('base.period') }} {{ $t('base.from') }}</label>
      <div class="custom-time-picker">
        <input
          type="text"
          v-model="startHour"
          @input="validateStartHour"
          @blur="formatStartHour"
          maxlength="2"
        />
        <span>:</span>
        <input
          type="text"
          v-model="startMinute"
          @input="validateStartMinute"
          @blur="formatMinute('startMinute', false)"
          maxlength="2"
        />
      </div>
    </div>
    <div class="time-picker">
      <label>{{ $t('base.period') }} {{ $t('base.to') }}</label>
      <div class="custom-time-picker">
        <input
          type="text"
          v-model="endHour"
          @input="validateEndHour"
          @blur="formatEndHour"
          maxlength="2"
        />
        <span>:</span>
        <input
          type="text"
          v-model="endMinute"
          @input="validateEndMinute"
          @blur="formatMinute('endMinute', false)"
          maxlength="2"
        />
      </div>
    </div>
    <p v-if="timeInvalid" class="error-text">
      {{ $t('errors.timeRangeInvalid') }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'TimeField',
  props: {
    from: {
      type: String,
      default: '00:00:00',
    },
    to: {
      type: String,
      default: '23:59:59',
    },
  },
  data () {
    const [fromHour, fromMinute] = this.from.split(':')
    const [toHour, toMinute] = this.to.split(':')

    return {
      startHour: fromHour.padStart(2, '0'),
      startMinute: fromMinute.padStart(2, '0'),
      endHour: toHour.padStart(2, '0'),
      endMinute: toMinute.padStart(2, '0'),
      timeInvalid: false,
    }
  },
  methods: {
    emitTimeRange () {
      // Always ensure "to" time is not earlier than "from" time
      this.adjustTimeRangeIfNeeded()

      // Get the formatted values for emission
      const startH = this.startHour === '' ? '00' : this.startHour.padStart(2, '0')
      const startM = this.startMinute === '' ? '00' : this.startMinute.padStart(2, '0')
      const endH = this.endHour === '' ? '00' : this.endHour.padStart(2, '0')
      const endM = this.endMinute === '' ? '00' : this.endMinute.padStart(2, '0')

      if (!this.timeInvalid) {
        this.$emit('updateTime',
          `${startH}:${startM}:00`,
          `${endH}:${endM}:00`
        )
      }
    },
    adjustTimeRangeIfNeeded () {
      // If any field is empty, skip the validation
      if (this.startHour === '' || this.startMinute === '' ||
          this.endHour === '' || this.endMinute === '') {
        this.timeInvalid = false
        return
      }

      // When the end hour is only a single digit (user is mid-typing), don't adjust yet
      // This allows entering "08" or "09" when the start hour is "07"
      if (this.endHour.length === 1 && this.endHour >= '0' && this.endHour <= '9') {
        this.timeInvalid = false
        return
      }

      const startHourNum = Number(this.startHour)
      const startMinuteNum = Number(this.startMinute)
      const endHourNum = Number(this.endHour)
      const endMinuteNum = Number(this.endMinute)

      // Check if from time is later than to time
      if (startHourNum > endHourNum ||
         (startHourNum === endHourNum && startMinuteNum >= endMinuteNum)) {
        // Automatically update end time to match start time
        this.endHour = this.startHour
        this.endMinute = this.startMinute
        this.timeInvalid = false
      } else {
        this.timeInvalid = false
      }
    },
    isTimeInvalid (startTime, endTime) {
      const [sh, sm] = startTime.split(':').map(Number)
      const [eh, em] = endTime.split(':').map(Number)

      return sh > eh || (sh === eh && sm >= em)
    },
    validateStartHour () {
      // Allow empty value during input
      if (this.startHour === '') return

      let value = Number(this.startHour)
      if (isNaN(value)) {
        this.startHour = ''
      } else if (value < 0) {
        this.startHour = '00'
      } else if (value > 23) {
        this.startHour = '23'
      }

      // After changing start hour, adjust end time if needed
      this.adjustTimeRangeIfNeeded()
    },
    validateStartMinute () {
      // Allow empty value during input
      if (this.startMinute === '') return

      this.formatMinute('startMinute', true)
      this.adjustTimeRangeIfNeeded()
    },
    validateEndHour () {
      // Allow empty value during input
      if (this.endHour === '') return

      // Don't validate single digit hour values while typing
      // This allows entering "08" when the start hour is "07"
      if (this.endHour.length === 1 && this.endHour >= '0' && this.endHour <= '9') {
        return
      }

      let value = Number(this.endHour)
      if (isNaN(value)) {
        this.endHour = ''
      } else if (value < 0) {
        this.endHour = '00'
      } else if (value > 23) {
        this.endHour = '23'
      }

      // After changing end hour, check if time range is valid
      this.adjustTimeRangeIfNeeded()
    },
    validateEndMinute () {
      // Allow empty value during input
      if (this.endMinute === '') return

      this.formatMinute('endMinute', true)
      this.adjustTimeRangeIfNeeded()
    },
    formatStartHour () {
      // If empty, set to default '00'
      if (this.startHour === '') {
        this.startHour = '00'
      } else {
        // First validate to ensure proper range
        this.validateStartHour()
        // Then format to ensure proper padding
        this.startHour = this.startHour.padStart(2, '0')
      }
      this.emitTimeRange()
    },
    formatEndHour () {
      // If empty, set to default '00'
      if (this.endHour === '') {
        this.endHour = '00'
      } else {
        // First validate to ensure proper range
        this.validateEndHour()
        // Then format to ensure proper padding
        this.endHour = this.endHour.padStart(2, '0')
      }
      this.emitTimeRange()
    },
    formatMinute (minuteField, allowEmpty = false) {
      if (this[minuteField] === '' && allowEmpty) return

      // If empty and not during input, set default
      if (this[minuteField] === '' && !allowEmpty) {
        this[minuteField] = '00'
        return
      }

      let minute = Number(this[minuteField])
      if (isNaN(minute)) {
        this[minuteField] = allowEmpty ? '' : '00'
      } else if (minute < 0) {
        this[minuteField] = '00'
      } else if (minute > 59) {
        this[minuteField] = '59'
      } else {
        // Only add padding when field loses focus or during emit
        if (!allowEmpty) {
          this[minuteField] = String(minute).padStart(2, '0')
        }
      }
      this.emitTimeRange()
    },
    formatHour (hour) {
      if (hour === '') return ''

      let value = Number(hour)
      if (isNaN(value)) return ''
      if (value < 0) value = 0
      if (value > 23) value = 23
      return String(value).padStart(2, '0')
    },
  },
  mounted () {
    this.adjustTimeRangeIfNeeded()
    this.emitTimeRange()
  },
}
</script>

<style scoped>
.time-picker-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.time-picker {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: space-between;
}
.time-picker label {
    font-size: 1.6rem;
    font-weight: 600;
}
.custom-time-picker {
  display: flex;
  align-items: center;
  gap: 5px;
}
input[type="text"] {
  background: #F4F4F4;
  color: #000;
  padding: 0.5rem;
  border-bottom: 1px solid #D8D8D8;
  font-size: 1.6rem;
  width: 40px;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
}
.error-text {
  color: red;
  font-size: 12px;
}
</style>